/**
 * AgentChatStore — plain TypeScript external store for agent chat episodes.
 *
 * Pure TypeScript, no React dependency. Uses AppTransport for transport abstraction.
 */

import {
  RpcError,
  type AppTransport,
} from "../contracts/transport.ts";
import { rpcNameForToolCall } from "../presentation/toolNames.ts";
import type { TokenBreakdown } from "../contracts/billing.ts";
import type {
  ChatMessage,
  ChatMessageAttachment,
  AgentFailure,
  CompletedToolResult,
  ContentBlock,
  EpisodeMessage,
  EpisodeState,
  PendingToolCall,
  ToolCallEvent,
  ToolResultEvent,
  UIContext,
} from "../types/episode.ts";

export type Listener = (state: EpisodeState) => void;

/**
 * Usage frame delivered via `chat.stream` with `stream.type === "usage"`.
 * `cost_micros === null` means the underlying llm_call was spooled (INV-BILL-17)
 * and the live cost is pending replay.
 */
export type UsageEvent = {
  /** Null when metering.start_turn failed and the chat continues ungated. */
  readonly turn_id: string | null;
  readonly episode_id: string | null;
  readonly provider: string;
  readonly model: string;
  readonly cumulative: TokenBreakdown;
  readonly cost_micros: number | null;
  readonly final: boolean;
  readonly step_index: number;
  readonly finish_reason: string | null;
  readonly error: string | null;
  readonly status: "ok" | "error";
};

export type UsageListener = (event: UsageEvent) => void;

/** One item of the episode session todo list (facet episodes.episode.todo). */
export type TodoItem = {
  readonly content: string;
  readonly active_form?: string;
  readonly status: "pending" | "in_progress" | "completed" | "cancelled";
};

/** Snapshot emitted on every accepted `episodes.todo.update` tool result. */
export type TodoSnapshot = {
  readonly contextKey: string;
  readonly episodeId: string | null;
  readonly items: readonly TodoItem[];
};

export type TodoListener = (snapshot: TodoSnapshot) => void;


/** Name of the whole-list-replace todo tool (MCP dot notation on the wire). */
const TODO_UPDATE_TOOL = "episodes.todo.update";

const TODO_STATUSES = new Set(["pending", "in_progress", "completed", "cancelled"]);

/**
 * INV-6 success predicate: a todo tool result counts as a valid snapshot
 * only when it parses as `{ items: TodoItem[] }` (statuses in enum,
 * contents non-empty strings). MCP error payloads and malformed shapes
 * return null — the caller keeps prior state.
 */
export function parseTodoItems(result: unknown): TodoItem[] | null {
  if (!result || typeof result !== "object") return null;
  const items = (result as Record<string, unknown>).items;
  if (!Array.isArray(items)) return null;
  const parsed: TodoItem[] = [];
  for (const raw of items) {
    if (!raw || typeof raw !== "object") return null;
    const o = raw as Record<string, unknown>;
    if (typeof o.content !== "string" || o.content.trim() === "") return null;
    if (typeof o.status !== "string" || !TODO_STATUSES.has(o.status)) return null;
    if (o.active_form !== undefined && typeof o.active_form !== "string") return null;
    parsed.push({
      content: o.content,
      ...(typeof o.active_form === "string" ? { active_form: o.active_form } : {}),
      status: o.status as TodoItem["status"],
    });
  }
  return parsed;
}

function numOrZero(o: Record<string, unknown>, key: string): number {
  const v = o[key];
  return typeof v === "number" ? v : 0;
}

const ZERO_TOKENS: TokenBreakdown = {
  input: 0,
  output: 0,
  cache_read: 0,
  cache_write: 0,
  cache_write_1h: 0,
  reasoning: 0,
};

/**
 * Parse a raw `usage` stream chunk into a `UsageEvent`. Returns null for
 * frames missing structural identity (provider/model/step_index). `turn_id`
 * may be null (degraded start_turn path) and `cumulative` may be absent
 * (step_error frames carry no tokens) — both are legitimate wire cases.
 */
function parseUsageEvent(chunk: unknown): UsageEvent | null {
  if (!chunk || typeof chunk !== "object") return null;
  const c = chunk as Record<string, unknown>;
  if (
    typeof c.provider !== "string" ||
    typeof c.model !== "string" ||
    typeof c.step_index !== "number"
  ) {
    console.warn("Dropping malformed usage frame:", chunk);
    return null;
  }
  const cumulative: TokenBreakdown =
    c.cumulative && typeof c.cumulative === "object"
      ? (() => {
          const co = c.cumulative as Record<string, unknown>;
          return {
            input: numOrZero(co, "input"),
            output: numOrZero(co, "output"),
            cache_read: numOrZero(co, "cache_read"),
            cache_write: numOrZero(co, "cache_write"),
            cache_write_1h: numOrZero(co, "cache_write_1h"),
            reasoning: numOrZero(co, "reasoning"),
          };
        })()
      : ZERO_TOKENS;
  return {
    turn_id: typeof c.turn_id === "string" ? c.turn_id : null,
    episode_id: typeof c.episode_id === "string" ? c.episode_id : null,
    provider: c.provider,
    model: c.model,
    cumulative,
    cost_micros: typeof c.cost_micros === "number" ? c.cost_micros : null,
    final: c.final === true,
    step_index: c.step_index,
    finish_reason: typeof c.finish_reason === "string" ? c.finish_reason : null,
    error: typeof c.error === "string" ? c.error : null,
    status: c.status === "error" ? "error" : "ok",
  };
}

/** The engine selection of one turn, replayed verbatim when that turn is
 *  resumed after an approval decision. */
type TurnParams = {
  readonly systemPrompt?: string;
  readonly engine?: string;
  readonly model?: string;
};

/**
 * Is this context still holding an unresolved approval?
 *
 * The client twin of the backend's `has_open_waits` (episodes/service.rs): an
 * approval-gated tool call is open until its card is approved or denied, and
 * the turn is not over until every one of them is. One turn can park several
 * — the engine aborts at the first `pending_approval`, but calls already in
 * flight in the same step still land — and resuming on the first decision
 * restarts the model over a transcript in which the rest never happened, so
 * it proposes them again. That is how approval cards stack up.
 *
 * @tested-by: tst_fe_agentcore_paused_011
 */
function hasOpenApproval(state: EpisodeState): boolean {
  return state.toolCalls.some((t) => t.approvalId !== undefined && t.status === "pending");
}

function freshState(): EpisodeState {
  return {
    episodeId: null, episodeTitle: null, replyTo: null, messages: [], streamingContent: "",
    isStreaming: false, pausedForApproval: false, toolCalls: [], toolResults: [],
    contentBlocks: [], error: null, resolvedEngine: null, warnings: [],
  };
}

function agentFailure(error: unknown): AgentFailure {
  if (error instanceof RpcError) {
    if (
      error.code === 402 &&
      error.data?.kind === "credit_exhausted"
    ) {
      return {
        kind: "credit_exhausted",
        code: 402,
        retryable: false,
      };
    }
    if (
      (error.code === 429 || error.code === 503) &&
      error.data !== null &&
      (error.data.kind.startsWith("provider_") ||
        error.data.kind === "hosted_agent_unavailable")
    ) {
      return {
        kind: "provider",
        code: error.code,
        providerKind: error.data.kind,
        retryable: error.data.retryable,
        displayMessage: error.data.display_message,
      };
    }
  }
  return { kind: "unexpected" };
}

function parseJson(raw: string | undefined): unknown {
  if (!raw) return {};
  try { return JSON.parse(raw); } catch { return { raw }; }
}

function mapToolStatus(s: string): PendingToolCall["status"] {
  const n = s.toLowerCase();
  if (n === "denied" || n === "rejected" || n === "failed") return "denied";
  if (n === "approved" || n === "done" || n === "complete" || n === "completed" || n === "sent" || n === "ok" || n === "success") return "approved";
  return "pending";
}

function isPersistedAskUserAwaitingResponse(
  toolName: string | undefined,
  parsedResult: Record<string, unknown> | null,
): boolean {
  if (!toolName || !parsedResult) return false;
  const normalized = toolName.toLowerCase();
  if (!normalized.includes("ask_user")) return false;
  return parsedResult.awaiting_response === true;
}

/**
 * `status` the backend stamps on an assistant row that carries INTERIM
 * narration — text flushed mid-turn, before the tool call that followed it
 * (plan docs/plans/split-2-episode-transcript-surfaces.md, D-1). Mirrors
 * `INTERIM_REASONING_STATUS` in `backend/src/modules/episodes/types.rs`.
 * @tested-by: tst_fe_agentcore_020
 */
const INTERIM_REASONING_STATUS = "interim_reasoning";

/**
 * One open wait of an episode, as `episodes.wait_set` returns it
 * (`backend/src/modules/episodes/types.rs::EpisodeWait`).
 */
interface EpisodeWaitDto {
  readonly kind: "approval" | "ask_user";
  readonly tool_call_id: string;
  readonly tool_name?: string;
  readonly tool_args?: string;
  readonly approval_id?: string;
}

/**
 * Add the episode's OPEN waits that the transcript did not carry.
 *
 * The transcript is a WINDOW; the wait set is a query over the whole
 * episode (plan docs/plans/split-2-episode-transcript-surfaces.md, D-7).
 * Without this, an approval parked past the window has no card to act on
 * after a reload (INV-6). Waits already rendered from the transcript are
 * left alone — the rows carry richer state than the wait row does.
 * @tested-by: tst_fe_agentcore_021
 */
async function appendOpenWaits(
  transport: AppTransport,
  episodeId: string,
  toolCallsById: Map<string, PendingToolCall>,
  orderedToolCallIds: string[],
  contentBlocks: ContentBlock[],
  chatName: string | undefined,
): Promise<void> {
  const response = await transport.rpc<{ waits: readonly EpisodeWaitDto[] }>(
    "episodes.wait_set",
    { episode_id: episodeId },
  );
  if (!Array.isArray(response.waits)) {
    throw new Error("episodes.wait_set returned no waits array");
  }
  for (const wait of response.waits) {
    if (!wait.tool_call_id || toolCallsById.has(wait.tool_call_id)) continue;
    toolCallsById.set(wait.tool_call_id, {
      id: wait.tool_call_id,
      name: wait.tool_name ?? "tool_call",
      args: parseJson(wait.tool_args),
      status: "pending",
      approvalId: wait.approval_id,
      chatName,
    });
    orderedToolCallIds.push(wait.tool_call_id);
    contentBlocks.push({ type: "tool_call", toolCallId: wait.tool_call_id });
  }
}

function hasToolPayload(message: EpisodeMessage): boolean {
  return Boolean(message.tool_name || message.tool_call_id || message.tool_args || message.tool_result);
}

function splitMessagesAtFirstToolTurn(
  messages: readonly EpisodeMessage[],
): { historyMessages: readonly EpisodeMessage[]; turnMessages: readonly EpisodeMessage[] } {
  const firstToolIdx = messages.findIndex(hasToolPayload);
  if (firstToolIdx < 0) {
    return { historyMessages: messages, turnMessages: [] };
  }

  let turnStartIdx = firstToolIdx;
  for (let i = firstToolIdx - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (!msg) continue;
    if (msg.role === "user") {
      turnStartIdx = i;
      break;
    }
  }

  return {
    historyMessages: messages.slice(0, turnStartIdx),
    turnMessages: messages.slice(turnStartIdx),
  };
}

export class AgentChatStore {
  private readonly states = new Map<string, EpisodeState>();
  private readonly listeners = new Map<string, Set<Listener>>();
  private readonly activeStreams = new Map<string, { controller: AbortController; episodeId: string }>();
  private readonly uiContexts = new Map<string, UIContext>();
  /** Keys where startNewEpisode was called — skip loadLatestEpisode */
  private readonly freshKeys = new Set<string>();
  /** Keys with in-flight episodes.create — prevents second sendMessage from skipping creation */
  private readonly pendingCreates = new Map<string, Promise<string | null>>();
  /**
   * Per-context engine selection of the turn currently in flight. A resume
   * after an approval continues THAT turn, so it must reach the same engine,
   * model and module system prompt — rebuilding the params from scratch sent
   * the continuation to whatever `DEFAULT_ENGINE` the backend holds.
   * @tested-by: tst_fe_agentcore_paused_012
   */
  private readonly turnParams = new Map<string, TurnParams>();
  /** Global subscribers to live `usage` stream frames (Stage 3) */
  private readonly usageListeners = new Set<UsageListener>();

  /**
   * Subscribe to live LLM usage frames emitted during `chat.stream`. Listener
   * receives one event per AI-SDK step (not cumulative per turn). Returns an
   * unsubscribe function.
   */
  onUsage(listener: UsageListener): () => void {
    this.usageListeners.add(listener);
    return () => {
      this.usageListeners.delete(listener);
    };
  }

  /** Global subscribers to session-todo snapshots (plan agent-todo-context-panel §B.3). */
  private readonly todoListeners = new Set<TodoListener>();
  /** Latest accepted todo snapshot per contextKey — late subscribers read it. */
  private readonly latestTodos = new Map<string, readonly TodoItem[]>();

  /**
   * Subscribe to live session-todo snapshots. Fires exactly when a
   * successful `episodes.todo.update` tool_result arrives (INV-6).
   * Returns an unsubscribe function.
   */
  onTodo(listener: TodoListener): () => void {
    this.todoListeners.add(listener);
    return () => {
      this.todoListeners.delete(listener);
    };
  }

  /** Latest accepted todo snapshot for a context, null before the first one. */
  getTodo(contextKey: string): readonly TodoItem[] | null {
    return this.latestTodos.get(contextKey) ?? null;
  }

  subscribe(contextKey: string, listener: Listener): () => void {
    if (!this.listeners.has(contextKey)) this.listeners.set(contextKey, new Set());
    this.listeners.get(contextKey)!.add(listener);
    const state = this.states.get(contextKey);
    if (state) listener(state);
    return () => { this.listeners.get(contextKey)?.delete(listener); };
  }

  getState(contextKey: string): EpisodeState | undefined {
    return this.states.get(contextKey);
  }

  /**
   * Plan item #7: for entity surfaces, hydrate the latest active/idle episode
   * for the entity via `episodes.list_for_entity(..., statuses=[active, idle], limit=5)`.
   * Ephemeral surfaces (new chat / search) pass `entityId=undefined` and skip.
   */
  async loadLatestEpisode(
    transport: AppTransport,
    contextKey: string,
    entityId?: string,
  ): Promise<string | null> {
    // Skip if user explicitly started a new episode for this key
    if (this.freshKeys.has(contextKey)) return null;
    // Ephemeral surfaces have no entity to list against — nothing to hydrate.
    if (!entityId) return null;

    try {
      const rows = await transport.rpc<
        readonly { episode_id: string; status: string; is_empty: boolean; updated_at: string }[]
      >(
        "episodes.list_for_entity",
        { entity_id: entityId, statuses: ["active", "idle"], limit: 5, offset: 0 },
      );

      if (rows.length === 0) return null;

      // Prefer the most-recent non-empty; fall back to rows[0] if all empty.
      const nonEmpty = rows.find((r) => !r.is_empty);
      const chosen = nonEmpty ?? rows[0];
      if (!chosen) return null;

      const detail = await transport.rpc<{
        id: string;
        title: string;
        /** Durable episode state. `needs_input` means the backend is still
         *  holding an unresolved wait — see the paused restore below. */
        status?: string;
        messages: readonly EpisodeMessage[];
        linked_entities?: readonly { id: string; name: string | null; schema_id: string; link_kind: string; created_at: string; data?: Record<string, unknown> }[];
      }>("episodes.get", { id: chosen.episode_id });

      const state = this.getOrCreate(contextKey);
      const toolCallsById = new Map<string, PendingToolCall>();
      const orderedToolCallIds: string[] = [];
      const toolResults: CompletedToolResult[] = [];
      const contentBlocks: ContentBlock[] = [];
      const uiCtx = this.uiContexts.get(contextKey);

      const { historyMessages } = splitMessagesAtFirstToolTurn(detail.messages);

      // Order fix: contentBlocks are the SINGLE chronological render stream —
      // the FULL transcript in ordinal order (tst_cc_order_001). state.messages
      // keeps its transcript semantics (history before the first tool turn).
      // Consecutive identical user rows collapse (trigger executor +
      // stream_chat double-persist); non-adjacent repeats are real turns and
      // must stay (PONG case).
      let prevUserText: string | null = null;
      for (const msg of detail.messages) {
        const isToolMsg = hasToolPayload(msg);

        if (!isToolMsg) {
          if ((msg.role === "user" || msg.role === "assistant") && msg.content?.trim()) {
            if (msg.role === "user") {
              if (prevUserText === msg.content) continue;
              prevUserText = msg.content;
              contentBlocks.push({ type: "user_message", text: msg.content });
            } else {
              prevUserText = null;
              // D-2: a row marked interim carries narration the engine
              // emitted mid-turn — reasoning, not a reply. It renders as a
              // `thinking` block, exactly where the live path would have
              // put it.
              contentBlocks.push(
                msg.status === INTERIM_REASONING_STATUS
                  ? { type: "thinking", text: msg.content }
                  : { type: "text", text: msg.content },
              );
            }
          }
          continue;
        }
        prevUserText = null;

        const parsedToolResult = msg.tool_result
          ? parseJson(msg.tool_result) as Record<string, unknown> | null
          : null;
        const persistedAskUserPending = isPersistedAskUserAwaitingResponse(
          msg.tool_name,
          parsedToolResult,
        );
        const toolCallId = msg.tool_call_id ?? `persisted-${msg.id}`;
        if (!toolCallsById.has(toolCallId)) {
          toolCallsById.set(toolCallId, {
            id: toolCallId, name: msg.tool_name || "tool_call",
            args: parseJson(msg.tool_args),
            status: persistedAskUserPending ? "pending" : mapToolStatus(msg.status),
            chatName: uiCtx?.selectedChatName,
          });
          orderedToolCallIds.push(toolCallId);
          contentBlocks.push({ type: "tool_call", toolCallId });
        } else {
          const current = toolCallsById.get(toolCallId)!;
          toolCallsById.set(toolCallId, {
            ...current,
            name: msg.tool_name || current.name,
            args: msg.tool_args ? parseJson(msg.tool_args) : current.args,
            status: persistedAskUserPending ? "pending" : mapToolStatus(msg.status),
          });
        }

        if (msg.tool_result) {
          const parsed = parsedToolResult;
          if (parsed && typeof parsed === "object" && parsed.pending_approval === true) {
            // MCP gate response — set approvalId, respect EXPLICIT status updates only.
            // "complete" is the default DB status for all messages — NOT an explicit approval.
            const tc = toolCallsById.get(toolCallId);
            if (tc) {
              const raw = msg.status.toLowerCase();
              const isExplicit = raw === "approved" || raw === "denied" || raw === "sent";
              toolCallsById.set(toolCallId, {
                ...tc,
                status: isExplicit ? mapToolStatus(msg.status) : "pending",
                approvalId: (parsed.approval_id as string) ?? undefined,
              });
            }
          } else {
            toolResults.push({ id: toolCallId, name: msg.tool_name || "tool_call", result: parsed });
          }
        }
      }

      try {
        await appendOpenWaits(
          transport,
          detail.id,
          toolCallsById,
          orderedToolCallIds,
          contentBlocks,
          uiCtx?.selectedChatName,
        );
      } catch (e) {
        // Surfaced, not swallowed: the transcript still renders, but the
        // user is one card short and the console says why.
        console.warn("Failed to read the episode wait set:", e);
      }

      state.episodeId = detail.id;
      state.episodeTitle = detail.title;
      const replyToLinked = detail.linked_entities?.find((e) => e.link_kind === "reply_to");
      state.replyTo = replyToLinked
        ? { entityId: replyToLinked.id, schemaId: replyToLinked.schema_id, name: replyToLinked.name ?? "Message", data: replyToLinked.data ?? {} }
        : null;
      state.messages = historyMessages
        .filter(
          (m: EpisodeMessage) =>
            m.role === "user" ||
            // Interim narration already renders as a thinking block; keeping
            // it here too would print it twice (D-2).
            (m.role === "assistant" && m.status !== INTERIM_REASONING_STATUS),
        )
        .map((m: EpisodeMessage) => {
          const content = m.content ?? "";
          if (m.role === "user") {
            const parsed = parseUserMessageAttachments(content);
            if (parsed) return { role: "user" as const, content, displayContent: parsed.displayContent, attachments: parsed.attachments };
          }
          return { role: m.role as "user" | "assistant", content };
        });
      state.toolCalls = orderedToolCallIds.map((id) => toolCallsById.get(id)).filter((tc): tc is PendingToolCall => tc !== undefined);
      state.toolResults = toolResults;
      state.contentBlocks = contentBlocks;
      // `pausedForApproval` gates resume after an approval decision, and it was
      // only ever set by the live `paused_for_approval` event — tab memory. A
      // reload or a reopened episode wiped it while the backend still had the
      // episode parked in `needs_input`, so approving executed the tool and
      // settled the wait but never continued the agent. The durable status is
      // the truth on load.
      state.pausedForApproval = detail.status === "needs_input";
      state.error = null;
      this.notify(contextKey);
      return detail.id;
    } catch (e) {
      console.warn("Failed to load latest episode:", e);
      return null;
    }
  }

  async loadEpisode(transport: AppTransport, contextKey: string, episodeId: string): Promise<string | null> {
    try {
      const detail = await transport.rpc<{
        id: string;
        title: string;
        /** Durable episode state. `needs_input` means the backend is still
         *  holding an unresolved wait — see the paused restore below. */
        status?: string;
        messages: readonly EpisodeMessage[];
        linked_entities?: readonly { id: string; name: string | null; schema_id: string; link_kind: string; created_at: string; data?: Record<string, unknown> }[];
      }>("episodes.get", { id: episodeId });

      const state = this.getOrCreate(contextKey);
      const toolCallsById = new Map<string, PendingToolCall>();
      const orderedToolCallIds: string[] = [];
      const toolResults: CompletedToolResult[] = [];
      const contentBlocks: ContentBlock[] = [];
      const uiCtx = this.uiContexts.get(contextKey);

      const { historyMessages } = splitMessagesAtFirstToolTurn(detail.messages);

      // Order fix: contentBlocks are the SINGLE chronological render stream —
      // the FULL transcript in ordinal order (tst_cc_order_001). state.messages
      // keeps its transcript semantics (history before the first tool turn).
      // Consecutive identical user rows collapse (trigger executor +
      // stream_chat double-persist); non-adjacent repeats are real turns and
      // must stay (PONG case).
      let prevUserText: string | null = null;
      for (const msg of detail.messages) {
        const isToolMsg = hasToolPayload(msg);

        if (!isToolMsg) {
          if ((msg.role === "user" || msg.role === "assistant") && msg.content?.trim()) {
            if (msg.role === "user") {
              if (prevUserText === msg.content) continue;
              prevUserText = msg.content;
              contentBlocks.push({ type: "user_message", text: msg.content });
            } else {
              prevUserText = null;
              // D-2: a row marked interim carries narration the engine
              // emitted mid-turn — reasoning, not a reply. It renders as a
              // `thinking` block, exactly where the live path would have
              // put it.
              contentBlocks.push(
                msg.status === INTERIM_REASONING_STATUS
                  ? { type: "thinking", text: msg.content }
                  : { type: "text", text: msg.content },
              );
            }
          }
          continue;
        }
        prevUserText = null;

        const parsedToolResult = msg.tool_result
          ? parseJson(msg.tool_result) as Record<string, unknown> | null
          : null;
        const persistedAskUserPending = isPersistedAskUserAwaitingResponse(
          msg.tool_name,
          parsedToolResult,
        );
        const toolCallId = msg.tool_call_id ?? `persisted-${msg.id}`;
        if (!toolCallsById.has(toolCallId)) {
          toolCallsById.set(toolCallId, {
            id: toolCallId, name: msg.tool_name || "tool_call",
            args: parseJson(msg.tool_args),
            status: persistedAskUserPending ? "pending" : mapToolStatus(msg.status),
            chatName: uiCtx?.selectedChatName,
          });
          orderedToolCallIds.push(toolCallId);
          contentBlocks.push({ type: "tool_call", toolCallId });
        } else {
          const current = toolCallsById.get(toolCallId)!;
          toolCallsById.set(toolCallId, {
            ...current,
            name: msg.tool_name || current.name,
            args: msg.tool_args ? parseJson(msg.tool_args) : current.args,
            status: persistedAskUserPending ? "pending" : mapToolStatus(msg.status),
          });
        }

        if (msg.tool_result) {
          const parsed = parsedToolResult;
          if (parsed && typeof parsed === "object" && parsed.pending_approval === true) {
            // MCP gate response — set approvalId, respect EXPLICIT status updates only.
            // "complete" is the default DB status for all messages — NOT an explicit approval.
            const tc = toolCallsById.get(toolCallId);
            if (tc) {
              const raw = msg.status.toLowerCase();
              const isExplicit = raw === "approved" || raw === "denied" || raw === "sent";
              toolCallsById.set(toolCallId, {
                ...tc,
                status: isExplicit ? mapToolStatus(msg.status) : "pending",
                approvalId: (parsed.approval_id as string) ?? undefined,
              });
            }
          } else {
            toolResults.push({ id: toolCallId, name: msg.tool_name || "tool_call", result: parsed });
          }
        }
      }

      try {
        await appendOpenWaits(
          transport,
          detail.id,
          toolCallsById,
          orderedToolCallIds,
          contentBlocks,
          uiCtx?.selectedChatName,
        );
      } catch (e) {
        // Surfaced, not swallowed: the transcript still renders, but the
        // user is one card short and the console says why.
        console.warn("Failed to read the episode wait set:", e);
      }

      state.episodeId = detail.id;
      state.episodeTitle = detail.title;
      const replyToLinked = detail.linked_entities?.find((e) => e.link_kind === "reply_to");
      state.replyTo = replyToLinked
        ? { entityId: replyToLinked.id, schemaId: replyToLinked.schema_id, name: replyToLinked.name ?? "Message", data: replyToLinked.data ?? {} }
        : null;
      state.messages = historyMessages
        .filter(
          (m: EpisodeMessage) =>
            m.role === "user" ||
            // Interim narration already renders as a thinking block; keeping
            // it here too would print it twice (D-2).
            (m.role === "assistant" && m.status !== INTERIM_REASONING_STATUS),
        )
        .map((m: EpisodeMessage) => {
          const content = m.content ?? "";
          if (m.role === "user") {
            const parsed = parseUserMessageAttachments(content);
            if (parsed) return { role: "user" as const, content, displayContent: parsed.displayContent, attachments: parsed.attachments };
          }
          return { role: m.role as "user" | "assistant", content };
        });
      state.toolCalls = orderedToolCallIds.map((id) => toolCallsById.get(id)).filter((tc): tc is PendingToolCall => tc !== undefined);
      state.toolResults = toolResults;
      state.contentBlocks = contentBlocks;
      // `pausedForApproval` gates resume after an approval decision, and it was
      // only ever set by the live `paused_for_approval` event — tab memory. A
      // reload or a reopened episode wiped it while the backend still had the
      // episode parked in `needs_input`, so approving executed the tool and
      // settled the wait but never continued the agent. The durable status is
      // the truth on load.
      state.pausedForApproval = detail.status === "needs_input";
      state.error = null;
      this.notify(contextKey);
      return detail.id;
    } catch (e) {
      console.warn("Failed to load episode:", e);
      return null;
    }
  }

  async sendMessage(transport: AppTransport, contextKey: string, text: string, context?: UIContext, existingEpisodeId?: string, contextEntityId?: string, attachments?: readonly ChatMessageAttachment[], displayContent?: string, episodeTitle?: string, systemPrompt?: string, engine?: string, model?: string, fileAttachmentIds?: readonly string[]): Promise<void> {
    const state = this.getOrCreate(contextKey);

    const existing = this.activeStreams.get(contextKey);
    if (existing) { existing.controller.abort(); this.activeStreams.delete(contextKey); }

    if (context) this.uiContexts.set(contextKey, context);
    // Remember this turn's engine selection — a resume after an approval
    // continues THIS turn and must reach the same engine.
    // @tested-by: tst_fe_agentcore_paused_012
    this.turnParams.set(contextKey, { systemPrompt, engine, model });

    // Add user message to state and notify IMMEDIATELY — before any RPC.
    const persistedContent = attachments?.length
      ? `${text}\n\n---attachments---\n${JSON.stringify(attachments)}`
      : text;
    state.messages = [...state.messages, {
      role: "user",
      content: persistedContent,
      displayContent: displayContent ?? undefined,
      attachments: attachments?.length ? attachments : undefined,
      fileAttachmentIds: fileAttachmentIds?.length ? fileAttachmentIds : undefined,
    }];
    state.streamingContent = "";
    state.isStreaming = true;
    state.pausedForApproval = false;
    state.error = null;
    // Fresh turn, fresh engine truth: the sidecar re-announces via
    // engine_resolved, and warnings describe only the current turn.
    state.resolvedEngine = null;
    state.warnings = [];
    // Keep pending tool calls as-is — user can still approve them after sending a new message.
    // Append user_message to contentBlocks so it renders AFTER old tool cards in Pass 2.
    // MessageList skipLast prevents it from also rendering in Pass 1.
    state.contentBlocks = [...state.contentBlocks, { type: "user_message", text: persistedContent }];
    this.notify(contextKey);

    // Now resolve episodeId — may involve awaiting RPCs
    const hadEpisodeId = Boolean(existingEpisodeId ?? state.episodeId);
    let episodeId = existingEpisodeId ?? state.episodeId;
    const mentionedIds = Array.from(
      new Set(
        (attachments ?? [])
          .filter((a) => (a.data as Record<string, unknown>)._replyTo !== true)
          .map((a) => (a.data as Record<string, unknown>).id as string | undefined)
          .filter((id): id is string => !!id && /^[0-9a-f]{8}-/i.test(id)),
      ),
    );

    const pendingCreate = this.pendingCreates.get(contextKey);
    if (pendingCreate) {
      episodeId = await pendingCreate;
    }

    if (!episodeId) {
      const episodeClientId = crypto.randomUUID();
      episodeId = episodeClientId;
      state.episodeId = episodeClientId;

      const createPromise = (async (): Promise<string | null> => {
        try {
          const title = episodeTitle ?? displayContent ?? (text.length > 60 ? text.slice(0, 60) + "..." : text);
          const entityId = contextEntityId && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(contextEntityId) ? contextEntityId : undefined;
          const replyToEntityId = context?.replyToEntityId;
          const createParams: Record<string, unknown> = {
            title,
            client_id: episodeClientId,
          };
          if (entityId) createParams.context_entity_id = entityId;
          if (mentionedIds.length > 0) createParams.mentioned_entity_ids = mentionedIds;
          if (replyToEntityId) createParams.reply_to_entity_id = replyToEntityId;
          const ep = await transport.rpc<{ id: string }>("episodes.create", createParams);
          state.episodeTitle = title;
          this.freshKeys.delete(contextKey);
          return ep.id;
        } catch (e) {
          state.episodeId = null;
          console.warn("Failed to create episode, continuing without persistence:", e);
          return null;
        } finally {
          this.pendingCreates.delete(contextKey);
        }
      })();

      this.pendingCreates.set(contextKey, createPromise);
      episodeId = await createPromise;
    }

    state.episodeId = episodeId;

    if (episodeId && mentionedIds.length > 0 && hadEpisodeId) {
      for (const entityId of mentionedIds) {
        transport.rpc("episodes.link_entity", {
          episode_id: episodeId,
          entity_id: entityId,
          kind: "mentions",
        }).catch((e: unknown) => { console.warn("Failed to link mentioned entity:", e); });
      }
    }

    const controller = this.streamChat(transport, state.messages, context, episodeId ?? undefined, contextKey, systemPrompt, engine, model);
    this.activeStreams.set(contextKey, { controller, episodeId: episodeId ?? "" });
  }

  /** Mark a tool call as approved without re-executing it (for inline-edited tools). */
  markToolCallDone(transport: AppTransport, _contextKey: string, toolCallId: string): void {
    // Search ALL states for the tool call — contextKey may not match exactly
    for (const [key, state] of this.states.entries()) {
      const found = state.toolCalls.some((t) => t.id === toolCallId);
      if (found) {
        state.toolCalls = state.toolCalls.map((t) => t.id === toolCallId ? { ...t, status: "approved" as const } : t);
        this.notify(key);
        transport.rpc("episodes.update_tool_status", { tool_call_id: toolCallId, status: "approved" }).catch((e: unknown) => { console.warn("Failed to persist tool status:", e); });
        return;
      }
    }
  }

  // @tested-by: tst_fe_agentcore_007, tst_fe_agentcore_008, tst_fe_agentcore_009
  async approveToolCall(transport: AppTransport, contextKey: string, toolCallId: string, approved: boolean, argumentsOverride?: unknown): Promise<void> {
    // Search ALL states for the tool call — contextKey may not match exactly
    let resolvedKey = contextKey;
    let state = this.states.get(contextKey);
    let tc = state?.toolCalls.find((t) => t.id === toolCallId);
    if (!tc) {
      for (const [key, s] of this.states.entries()) {
        const found = s.toolCalls.find((t) => t.id === toolCallId);
        if (found) { resolvedKey = key; state = s; tc = found; break; }
      }
    }
    if (!state) return;

    if (approved && tc?.status === "approved") {
      return;
    }

    if (!approved) {
      // S0 (docs/plans/episode-needs-input-propagation.md): approvalId-
      // backed Deny goes through the tool_approvals.deny RPC — the SINGLE
      // durable writer. No episodes.update_tool_status (it mutates every
      // row with that tool_call_id), no synthetic "[User denied]" message
      // (its append_message → active side effect contradicts the resolved
      // state), and NO resumeIfPausedForApproval: resume replays
      // s.messages, and without the synthetic message the latest user
      // message is the ORIGINAL request, which CLI resume mode would
      // re-select and the backend would re-persist. Deny clears the
      // paused/streaming state, updates the card, and stops.
      // @tested-by: tst_fe_agentcore_014, tst_fe_agentcore_015,
      //             tst_fe_agentcore_paused_004
      if (tc?.approvalId) {
        try {
          await transport.rpc("tool_approvals.deny", {
            id: tc.approvalId,
            episode_id: state.episodeId ?? undefined,
            tool_call_id: state.episodeId ? toolCallId : undefined,
          });
        } catch (e) {
          state.error = agentFailure(e);
          this.notify(resolvedKey);
          throw e;
        }
        state.toolCalls = state.toolCalls.map((t) => t.id === toolCallId ? { ...t, status: "denied" as const } : t);
        // Denying ONE of several parked writes does not end the turn — the
        // backend keeps the episode in `needs_input` until its wait-set is
        // empty. Clearing the pause here stranded the siblings: approving the
        // last one afterwards found nothing to resume.
        // @tested-by: tst_fe_agentcore_paused_011
        if (!hasOpenApproval(state)) state.pausedForApproval = false;
        state.isStreaming = false;
        const existing = this.activeStreams.get(resolvedKey);
        if (existing) {
          existing.controller.abort();
          this.activeStreams.delete(resolvedKey);
        }
        this.notify(resolvedKey);
        return;
      }
      // Legacy/non-approvalId path (e.g. module prompts): unchanged.
      state.toolCalls = state.toolCalls.map((t) => t.id === toolCallId ? { ...t, status: "denied" as const } : t);
      // Inject deny context into conversation so the agent knows the user's decision.
      const toolLabel = tc ? `${tc.name}(${JSON.stringify(tc.args)})` : toolCallId;
      const denyMsg = `[User denied tool: ${toolLabel}]`;
      state.messages = [...state.messages, { role: "user", content: denyMsg }];
      state.contentBlocks = [...state.contentBlocks, { type: "user_message", text: denyMsg }];
      this.notify(resolvedKey);
      // Persist denied status to DB
      transport.rpc("episodes.update_tool_status", { tool_call_id: toolCallId, status: "denied" }).catch((e: unknown) => { console.warn("Failed to persist tool status:", e); });
      // Persist deny message to episode history
      if (state.episodeId) {
        transport.rpc("episodes.append_message", { episode_id: state.episodeId, role: "user", content: denyMsg }).catch((e: unknown) => { console.warn("Failed to persist deny message:", e); });
      }
      // Resume the paused agent (e.g. codex exec) so it continues AFTER the denial,
      // seeing the "[User denied]" message — otherwise it stays paused_for_approval
      // forever and the whole turn hangs.
      this.resumeIfPausedForApproval(transport, resolvedKey);
      return;
    }

    // Keep naming explicit in this flow:
    // - toolCallId: feed/tool_call id persisted in episodes
    // - approvalRequestId: transient approval-store id used by tool_approvals.execute
    const approvalRequestId = tc?.approvalId;
    const executeEpisodeId = state?.episodeId ?? undefined;
    const executeToolCallId = executeEpisodeId ? toolCallId : undefined;

    // Build execute params — include arguments_override for batch edits/exclusions
    const executeParams: Record<string, unknown> = {
      id: approvalRequestId,
      episode_id: executeEpisodeId,
      tool_call_id: executeToolCallId,
    };
    if (argumentsOverride != null) {
      executeParams.arguments_override = argumentsOverride;
    }
    const effectiveArgs = (argumentsOverride ?? tc?.args) as Record<string, unknown>;

    // Optimistic in-flight lock: flip the card to "approved" BEFORE the
    // round-trip so the guard above rejects any further click / re-render that
    // fires a second tool_approvals.execute while this one is in flight (that
    // window is what created duplicate notes/sends). The catch below reverts to
    // "pending" if the RPC fails, so a genuine failure stays retryable.
    {
      const s1 = this.states.get(resolvedKey);
      if (s1) {
        s1.toolCalls = s1.toolCalls.map((t) => t.id === toolCallId ? { ...t, status: "approved" as const } : t);
        this.notify(resolvedKey);
      }
    }

    try {
      let rpcResult: unknown;
      if (tc?.approvalId) {
        // Approval gate is authoritative — no fallback to direct tool RPC.
        // If this fails, the user must retry or request a fresh approval.
        rpcResult = await transport.rpc("tool_approvals.execute", executeParams);
      } else if (tc) {
        // No approval gate — execute tool directly via the canonical
        // first-underscore normalizer (A-3, presentation/toolNames.ts).
        rpcResult = await transport.rpc(rpcNameForToolCall(tc.name), effectiveArgs);
      } else {
        rpcResult = await transport.rpc("chat.tool_approve", { tool_call_id: toolCallId, approved });
      }
      // RPC succeeded — now mark as approved and capture result
      const s2 = this.states.get(resolvedKey);
      if (s2) {
        s2.toolCalls = s2.toolCalls.map((t) => t.id === toolCallId ? { ...t, status: "approved" as const } : t);
        if (rpcResult != null && tc) {
          s2.toolResults = [...s2.toolResults, { id: toolCallId, name: tc.name, result: rpcResult }];
        }
        this.notify(resolvedKey);
      }
      // Persist approved status to DB
      transport.rpc("episodes.update_tool_status", { tool_call_id: toolCallId, status: "approved" }).catch((e: unknown) => { console.warn("Failed to persist tool status:", e); });

      // Resume the agent stream — ONLY if the last stream emitted
      // `paused_for_approval`. Engines/mocks that finish naturally don't set the
      // flag, so approval on an already-completed stream does not re-stream.
      this.resumeIfPausedForApproval(transport, resolvedKey);
    } catch (e) {
      console.warn("Failed to execute tool approval:", e);
      const s = this.states.get(resolvedKey);
      if (s) {
        s.toolCalls = s.toolCalls.map((t) => t.id === toolCallId ? { ...t, status: "pending" as const } : t);
        s.error = agentFailure(e);
        this.notify(resolvedKey);
      }
      throw e;
    }
  }

  /**
   * Resume the agent stream after an approval decision — but ONLY if the last
   * stream paused for approval (`paused_for_approval`). Used by BOTH approve and
   * deny: on approve the executed result is in the transcript, on deny the
   * "[User denied]" message is — either way the engine (e.g. `codex exec resume`)
   * continues and reacts. No-op when the stream already finished naturally, so it
   * never triggers a redundant re-stream.
   */
  private resumeIfPausedForApproval(transport: AppTransport, resolvedKey: string): void {
    const s = this.states.get(resolvedKey);
    if (!s?.episodeId || !s.pausedForApproval) return;
    // One decision is not the decision on the TURN. While another gated write
    // is still waiting on its card, the backend keeps the episode in
    // `needs_input` — resuming here would restart the model over a transcript
    // in which that write never happened, and it would propose it again.
    // Wait for the wait-set to empty, exactly as `settle_needs_input` does.
    // @tested-by: tst_fe_agentcore_paused_011
    if (hasOpenApproval(s)) return;
    s.pausedForApproval = false;
    const existing = this.activeStreams.get(resolvedKey);
    if (existing) {
      existing.controller.abort();
      this.activeStreams.delete(resolvedKey);
    }
    s.streamingContent = "";
    s.isStreaming = true;
    s.error = null;
    this.notify(resolvedKey);
    const ctx = this.uiContexts.get(resolvedKey);
    // Continue the SAME turn: same engine, same model, same module system
    // prompt. Rebuilding the params from scratch handed the continuation to
    // the backend's default engine — a different agent reading the approved
    // write than the one that proposed it.
    // @tested-by: tst_fe_agentcore_paused_012
    const turn = this.turnParams.get(resolvedKey);
    const controller = this.streamChat(
      transport,
      s.messages,
      ctx,
      s.episodeId,
      resolvedKey,
      turn?.systemPrompt,
      turn?.engine,
      turn?.model,
    );
    this.activeStreams.set(resolvedKey, { controller, episodeId: s.episodeId });
  }

  /** Alias: staging's API shape for loading an episode. */
  async loadEpisodeById(transport: AppTransport, contextKey: string, episodeId: string): Promise<void> {
    this.clearTodoCache(contextKey);
    await this.loadEpisode(transport, contextKey, episodeId);
  }

  /** @internal review fix: a context switching episodes must not serve the
   * previous episode's todo snapshot via getTodo. */
  private clearTodoCache(contextKey: string): void {
    this.latestTodos.delete(contextKey);
  }

  async startNewEpisode(transport: AppTransport, contextKey: string, presetEpisodeId?: string): Promise<void> {
    this.clearTodoCache(contextKey);
    const stream = this.activeStreams.get(contextKey);
    if (stream) { stream.controller.abort(); this.activeStreams.delete(contextKey); }

    const state = this.states.get(contextKey);
    if (state?.episodeId) {
      try { await transport.rpc("episodes.complete", { id: state.episodeId }); } catch (e) { console.warn("Failed to complete episode:", e); }
    }

    this.freshKeys.add(contextKey);
    const fresh = freshState();
    if (presetEpisodeId) {
      fresh.episodeId = presetEpisodeId;
      fresh.episodeTitle = "New chat";
    }
    this.states.set(contextKey, fresh);
    this.notify(contextKey);
  }

  stopStream(contextKey: string): void {
    const stream = this.activeStreams.get(contextKey);
    if (stream) { stream.controller.abort(); this.activeStreams.delete(contextKey); }
    const state = this.states.get(contextKey);
    if (state) {
      state.isStreaming = false;
      if (state.streamingContent) {
        state.messages = [...state.messages, { role: "assistant", content: state.streamingContent }];
        state.streamingContent = "";
      }
      this.notify(contextKey);
    }
  }

  private notify(contextKey: string): void {
    const state = this.states.get(contextKey);
    if (!state) return;
    const set = this.listeners.get(contextKey);
    if (set) for (const l of set) l(state);
  }

  private getOrCreate(contextKey: string): EpisodeState {
    if (!this.states.has(contextKey)) this.states.set(contextKey, freshState());
    return this.states.get(contextKey)!;
  }

  private streamChat(
    transport: AppTransport,
    messages: readonly ChatMessage[],
    context: UIContext | undefined,
    episodeId: string | undefined,
    contextKey: string,
    systemPrompt?: string,
    engine?: string,
    model?: string,
  ): AbortController {
    const controller = new AbortController();
    const params: Record<string, unknown> = {
      // `attachments` is the file-entity UUID channel, validated by the
      // backend before the stream starts (services/agents/attachments.rs).
      // Entity-mention attachments travel folded into `content` instead.
      messages: messages.map((m) => ({
        role: m.role,
        content: m.content,
        ...(m.fileAttachmentIds?.length ? { attachments: m.fileAttachmentIds } : {}),
      })),
      temperature: 0.7,
      context,
      episode_id: episodeId,
      ...(systemPrompt ? { system_prompt: systemPrompt } : {}),
      ...(engine ? { engine } : {}),
      ...(model ? { model } : {}),
    };

    let aborted = false;
    controller.signal.addEventListener("abort", () => { aborted = true; });

    transport
      .rpcStream("chat.stream", params, (chunk: unknown) => {
        if (aborted) return;
        const event = chunk as {
          type?: string; content?: string; full_content?: string;
          message?: string; id?: string; name?: string;
          args?: unknown; result?: unknown; episode_id?: string;
          engine?: string; code?: string;
        };
        switch (event.type) {
          case "engine_resolved":
            if (typeof event.engine === "string") {
              this.handleEngineResolved(contextKey, event.engine);
            }
            break;
          case "warning":
            // Wire contract makes `code` mandatory (agent engines/types.ts);
            // a frame without it is malformed — drop it, don't synthesize.
            if (typeof event.code === "string" && typeof event.message === "string") {
              this.handleWarning(contextKey, event.code, event.message);
            }
            break;
          case "delta":
            if (event.content != null) this.handleDelta(contextKey, event.content);
            break;
          case "error":
            this.handleStreamError(contextKey, event.message ?? "Unknown error");
            break;
          case "tool_call":
            this.handleToolCall(contextKey, { id: event.id!, name: event.name!, args: event.args });
            break;
          case "tool_result":
            this.handleToolResult(contextKey, { id: event.id!, name: event.name, result: event.result });
            break;
          case "usage":
            this.handleUsage(chunk);
            break;
          case "paused_for_approval":
            this.handlePausedForApproval(contextKey);
            break;
        }
      })
      .then((result) => {
        if (aborted) return;
        const done = result as { type?: string; full_content?: string };
        if (done?.full_content != null) this.handleDone(contextKey, done.full_content);
      })
      .catch((err: unknown) => {
        if (aborted) return;
        this.handleStreamError(contextKey, err);
      });

    return controller;
  }

  /** @internal — exposed for tests */
  handleEngineResolved(contextKey: string, engine: string): void {
    const s = this.states.get(contextKey);
    if (s) { s.resolvedEngine = engine; this.notify(contextKey); }
  }

  /** @internal — exposed for tests */
  handleWarning(contextKey: string, code: string, message: string): void {
    const s = this.states.get(contextKey);
    if (s) { s.warnings = [...(s.warnings ?? []), { code, message }]; this.notify(contextKey); }
  }

  private handleDelta(contextKey: string, content: string): void {
    const s = this.states.get(contextKey);
    if (s) { s.streamingContent += content; this.notify(contextKey); }
  }

  /** @internal — exposed for tests. Marks the episode as paused so the
   *  next approveToolCall resumes the stream. Cleared on resume, on the
   *  next sendMessage, or on starting a fresh episode. */
  handlePausedForApproval(contextKey: string): void {
    const s = this.states.get(contextKey);
    if (s) { s.pausedForApproval = true; this.notify(contextKey); }
  }

  /** @internal — exposed for tests */
  handleDone(contextKey: string, fullContent: string): void {
    const s = this.states.get(contextKey);
    if (s) {
      // Order fix (tst_cc_order_003): blocks carry the assistant reply even
      // when no deltas streamed (full_content-only engines) — blocks are the
      // sole render source once non-empty.
      const reply = s.streamingContent.trim() ? s.streamingContent : fullContent;
      if (reply.trim()) {
        s.contentBlocks = [...s.contentBlocks, { type: "text", text: reply }];
      }
      // A turn that stopped at a gated write produced no text, so `done`
      // closes it with `full_content: ""`. Appending that as an assistant
      // message left the transcript ending in an empty assistant turn — and
      // resume replays this array verbatim, which Anthropic rejects outright.
      // The transcript records what was said; nothing was said.
      // @tested-by: tst_fe_agentcore_paused_013
      if (reply.trim()) {
        s.messages = [...s.messages, { role: "assistant", content: reply }];
      }
      s.streamingContent = "";
      s.isStreaming = false;
      this.notify(contextKey);
    }
    this.activeStreams.delete(contextKey);
  }

  private handleStreamError(contextKey: string, error: unknown): void {
    const s = this.states.get(contextKey);
    if (s) {
      s.error = agentFailure(error);
      s.isStreaming = false;
      if (s.streamingContent) {
        s.contentBlocks = [...s.contentBlocks, { type: "text", text: s.streamingContent + " [interrupted]" }];
        s.messages = [...s.messages, { role: "assistant", content: s.streamingContent + " [interrupted]" }];
        s.streamingContent = "";
      }
      this.notify(contextKey);
    }
    this.activeStreams.delete(contextKey);
  }

  /** @internal — dispatch a raw `usage` stream chunk to onUsage subscribers. */
  private handleUsage(chunk: unknown): void {
    if (this.usageListeners.size === 0) return;
    const event = parseUsageEvent(chunk);
    if (!event) return;
    for (const l of this.usageListeners) {
      try {
        l(event);
      } catch (err) {
        console.warn("UsageListener threw:", err);
      }
    }
  }

  /** @internal — exposed for tests */
  handleToolCall(contextKey: string, event: ToolCallEvent): void {
    const s = this.states.get(contextKey);
    if (s) {
      const newBlocks = [...s.contentBlocks];
      if (s.streamingContent.trim()) newBlocks.push({ type: "thinking", text: s.streamingContent });
      newBlocks.push({ type: "tool_call", toolCallId: event.id });
      s.contentBlocks = newBlocks;
      s.streamingContent = "";
      const uiCtx = this.uiContexts.get(contextKey);
      s.toolCalls = [...s.toolCalls, { id: event.id, name: event.name, args: event.args, status: "pending", chatName: uiCtx?.selectedChatName }];

      // Update episode title when agent calls set_title
      if (event.name.includes("set_title")) {
        const args = event.args as Record<string, unknown> | undefined;
        if (args?.title && typeof args.title === "string") {
          s.episodeTitle = args.title;
        }
      }

      this.notify(contextKey);
    }
  }

  /** @internal — exposed for tests */
  handleToolResult(contextKey: string, event: ToolResultEvent): void {
    const s = this.states.get(contextKey);
    if (!s) return;
    // Session todo: an accepted whole-list replace rides the normal
    // tool_result stream (plan §B.3). Only the update tool emits (INV-6) —
    // reads and other tools never do, even with items-shaped results.
    if (event.name === TODO_UPDATE_TOOL) {
      const items = parseTodoItems(event.result);
      if (items) {
        this.latestTodos.set(contextKey, items);
        const snapshot: TodoSnapshot = {
          contextKey,
          episodeId: s.episodeId,
          items,
        };
        for (const l of this.todoListeners) {
          try {
            l(snapshot);
          } catch (err) {
            console.error("onTodo listener threw:", err);
          }
        }
      }
    }
    const resultObj = event.result as Record<string, unknown> | undefined;
    if (resultObj && typeof resultObj === "object" && resultObj.pending_approval === true) {
      s.toolCalls = s.toolCalls.map((tc) =>
        tc.id === event.id ? { ...tc, approvalId: (resultObj.approval_id as string) ?? undefined } : tc,
      );
    } else {
      const toolName = event.name ?? s.toolCalls.find((tc) => tc.id === event.id)?.name ?? "";
      const askUserAwaitingResponse = isPersistedAskUserAwaitingResponse(
        toolName,
        resultObj ?? null,
      );
      // @tested-by: tst_fe_agentcore_012, AgentChatStore — live ask_user pending state
      // @invariant: a live tool_result means the tool already executed; do not leave it approvable.
      // ask_user is the exception: its tool_result only means the question was
      // sent, and the prompt remains pending until the user answers.
      s.toolCalls = s.toolCalls.map((tc) =>
        tc.id === event.id ? { ...tc, status: askUserAwaitingResponse ? "pending" : "approved" as const } : tc,
      );
      s.toolResults = [...s.toolResults, { id: event.id, name: toolName, result: event.result }];
    }
    this.notify(contextKey);
  }
}

// ─── Persisted message attachment parser ─────────────────────────────

const ATTACHMENTS_SEPARATOR = "\n\n---attachments---\n";

export function parseUserMessageAttachments(
  content: string,
): { displayContent: string; attachments: ChatMessageAttachment[] } | null {
  const sepIdx = content.indexOf(ATTACHMENTS_SEPARATOR);
  if (sepIdx === -1) return null;
  const textPart = content.slice(0, sepIdx);
  const jsonPart = content.slice(sepIdx + ATTACHMENTS_SEPARATOR.length);
  try {
    const attachments = JSON.parse(jsonPart) as ChatMessageAttachment[];
    if (!Array.isArray(attachments) || attachments.length === 0) return null;
    return { displayContent: textPart, attachments };
  } catch {
    return null;
  }
}
